import React, { useState } from "react";

import { Spinner, Text } from "@performyard/py-components";

import { DisconnectionModal } from "./DisconnectionModal";
import { ProviderConnectionCard } from "./ProviderConnectionCard";
import { ZoomIcon } from "./ZoomIcon";

import {
	useDisconnectVideoConferencingIntegration,
	useMeetingsIntegrationsAvailableProviders,
	useUserVideoConferencingStatus,
	useVideoConferencingOAuthUrl,
} from "../../../../../api/hooks/defs/meetings-integrations";

import { VideoConferencingProvider } from "../config";

const PROVIDER_ICONS: Record<VideoConferencingProvider, React.ReactNode> = {
	zoom: <ZoomIcon size={40} />,
};

type DisconnectTarget = { provider: VideoConferencingProvider; displayName: string } | null;

/** Video Conferencing section — renders one card per provider from the server registry. */
export const VideoConferencingSection = (): React.ReactElement => {
	const { initiateVideoConferencingAuth: initiateZoomAuth, isLoading: isZoomAuthLoading } =
		useVideoConferencingOAuthUrl({ provider: "zoom" });

	const { mutateAsync: disconnectIntegration, isPending: isDisconnecting } =
		useDisconnectVideoConferencingIntegration();

	const { data: providersData, isLoading: isProvidersLoading } = useMeetingsIntegrationsAvailableProviders();
	const { data: statusData, isLoading: isStatusLoading } = useUserVideoConferencingStatus();

	const [disconnectTarget, setDisconnectTarget] = useState<DisconnectTarget>(null);

	const connectedConnection = statusData?.connection ?? null;

	const handleConnect = (provider: VideoConferencingProvider) => {
		if (provider === "zoom") {
			initiateZoomAuth();
		}
	};

	const handleDisconnectRequest = (provider: VideoConferencingProvider, displayName: string) => {
		setDisconnectTarget({ provider, displayName });
	};

	const handleConfirmDisconnect = async () => {
		if (!disconnectTarget) return;
		await disconnectIntegration({ provider: disconnectTarget.provider });
		setDisconnectTarget(null);
	};

	const isInitialLoading = isProvidersLoading || isStatusLoading;

	return (
		<div className="flex w-full flex-col gap-4">
			<div className="flex w-[656px] flex-col gap-1">
				<Text size="paragraph-2" weight="semi-bold">
					Video Conferencing &amp; Transcripts
				</Text>
				<Text size="paragraph-3" weight="regular">
					Connect Zoom to automatically generate meeting links and capture transcripts for coaching insights
					and action items.
				</Text>
			</div>

			{isInitialLoading ? (
				<div className="flex h-[120px] w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{providersData?.providers.map(({ provider, displayName }) => {
						const isThisProviderConnected = connectedConnection?.provider === provider;
						return (
							<ProviderConnectionCard
								key={provider}
								provider={provider}
								displayName={displayName}
								icon={PROVIDER_ICONS[provider as VideoConferencingProvider] ?? null}
								isConnected={isThisProviderConnected}
								connectedAs={
									isThisProviderConnected ? (connectedConnection.accountEmail ?? null) : null
								}
								isDisabled={isZoomAuthLoading || isDisconnecting}
								onConnect={() => handleConnect(provider as VideoConferencingProvider)}
								onDisconnect={() =>
									handleDisconnectRequest(provider as VideoConferencingProvider, displayName)
								}
							/>
						);
					})}
				</div>
			)}

			<DisconnectionModal
				isOpen={disconnectTarget !== null}
				title={`Disconnect ${disconnectTarget?.displayName ?? ""}?`}
				bodyText={`New meetings won't include a ${disconnectTarget?.displayName ?? ""} link and we'll no longer be able to fetch meeting transcripts. Existing meetings won't be affected.`}
				isDisconnecting={isDisconnecting}
				handleConfirmDisconnect={handleConfirmDisconnect}
				handleCancelDisconnect={() => setDisconnectTarget(null)}
			/>
		</div>
	);
};
